import { useState } from 'react';
import { formatDate, formatTime } from '../../../lib/format';
import type { ImpersonationRow } from '@dru/types';
import { Alert, Checkbox, DataTable, IconAction, RowActions, StatusBadge } from '@dru/ui';
import { LogOut } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';

/**
 * Who is acting as whom, and why.
 *
 * Impersonation is the sharpest tool in the product: a platform administrator
 * signing in as somebody inside a company, to see — or, in a full-access
 * session, do — what they do. Since 29 Oct 2026 it is one click (the owner
 * works as users), lasts up to eight hours, and a reason is optional: the
 * server records a default sentence when none is given. None of that is
 * oversight. Oversight is this page, where the sessions are visible to
 * somebody other than the person who started them, and can be cut short.
 *
 * Each row says which kind of session it was. A full-access session is the
 * one that could change a customer's records, so it is the one a reader
 * should be able to pick out; the page used to say every session was read
 * only, which stopped being true when assisted setup arrived.
 *
 * Ended sessions stay on the list. A control that only shows what is
 * happening right now cannot answer "who looked at this company last month".
 */
export function Impersonation() {
  const { permissions } = useAuth();
  const [activeOnly, setActiveOnly] = useState(false);
  const [ending, setEnding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canEnd = holds(permissions, 'platform.impersonate');

  const sessions = useQuery<ImpersonationRow[]>(
    () => api.get(`/admin/impersonation?activeOnly=${activeOnly}&limit=100`),
    [activeOnly],
  );

  const live = (sessions.data ?? []).filter((s) => s.active);

  async function end(id: string) {
    setEnding(id);
    setError(null);
    try {
      await api.post(`/admin/impersonation/${id}/end`, {});
      sessions.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setEnding(null);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Impersonation"
        hint={(
          <>
            <p>
              Every session in which a platform administrator signed in as somebody else. A session lasts up to
              8 hours unless ended sooner, and the audit trail records the real person throughout.
              Signing a medical record is never possible inside one.
            </p>
            <p className="mt-2">
              In a read-only session every write is refused at the server, not merely hidden. Ending a session here
              revokes it immediately rather than waiting for it to run out.
            </p>
          </>
        )}
      />

      {live.length > 0 && (
        <Alert tone="warning">
          {live.length === 1 ? 'One session is' : `${live.length} sessions are`} running now.
          {canEnd ? ' End any of them from its row; it stops at once.' : ''}
        </Alert>
      )}
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="flex flex-wrap items-center gap-3">
        <Checkbox
          label="Only sessions running now"
          checked={activeOnly}
          onChange={(e) => setActiveOnly(e.target.checked)}
        />
      </div>

      <DataTable<ImpersonationRow>
        rows={sessions.data ?? []}
        loading={sessions.loading}
        error={sessions.error}
        onRetry={sessions.reload}
        rowKey={(s) => s.id}
        empty={{
          title: activeOnly ? 'No session is running' : 'No sessions yet',
          hint: activeOnly
            ? 'Clear the filter to see sessions that have ended.'
            : 'Sessions appear here when an administrator uses Sign in as… on a company user or a platform operator.',
        }}
        columns={[
          { key: 'as', header: 'Signed in as', alwaysVisible: true, cell: (s) => (
            <div className="min-w-0">
              <div className="text-body text-neutral-900">{s.target.fullName}</div>
              <div className="truncate text-caption font-normal text-neutral-500">
                {s.target.email}{s.targetRoles.length ? ` · ${s.targetRoles.map(roleWords).join(', ')}` : ''}
              </div>
            </div>
          ) },
          { key: 'who', header: 'By', cell: (s) => (
            <div className="min-w-0">
              <div className="text-small text-neutral-900">{s.actor.fullName}</div>
              <div className="truncate text-caption font-normal text-neutral-500">{s.actor.email}</div>
            </div>
          ) },
          { key: 'mode', header: 'Access', cell: (s) => (
            // A read-only session is minted with the write permissions
            // stripped; a full-access one strips nothing.
            s.strippedPermissions.length === 0
              ? <StatusBadge tone="warning">Full access</StatusBadge>
              : <StatusBadge tone="neutral">Read only</StatusBadge>
          ) },
          { key: 'reason', header: 'Reason', cell: (s) => (
            // The one-click path records the server's default sentence; saying
            // "no reason given" once is clearer than repeating it on every row.
            <div className="max-w-xs text-small">
              {!s.reason || s.reason === ONE_CLICK_REASON
                ? <span className="text-neutral-500">No reason given</span>
                : <span className="text-neutral-700">{s.reason}</span>}
              {s.ticketRef && <span className="font-mono text-neutral-500"> · {s.ticketRef}</span>}
            </div>
          ) },
          { key: 'when', header: 'Started', hideOnMobile: true, cell: (s) => (
            <div className="whitespace-nowrap text-small text-neutral-600">
              <div>{formatDate(s.startedAt)}</div>
              <div className="text-caption text-neutral-500">{formatTime(s.startedAt)}</div>
            </div>
          ) },
          { key: 'reqs', header: 'Requests', align: 'right', hideOnMobile: true, cell: (s) => (
            <span className="tabular-nums text-small text-neutral-600">{s.requestCount}</span>
          ) },
          { key: 'state', header: 'State', mobileLabel: false, cell: (s) => (
            s.active
              ? <StatusBadge tone="pending">Running</StatusBadge>
              : <StatusBadge>{s.endedAt ? 'Ended' : 'Expired'}</StatusBadge>
          ) },
          { key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (s) => (
            s.active && canEnd ? (
              <RowActions>
                <IconAction
                  label={`End the session as ${s.target.fullName}`}
                  icon={LogOut}
                  danger
                  loading={ending === s.id}
                  onClick={() => end(s.id)}
                />
              </RowActions>
            ) : null
          ) },
        ]}
      />

    </div>
  );
}

/** What the server records when "Sign in as" is used without a reason (impersonation.service.ts). */
const ONE_CLICK_REASON = 'Signed in as this user by a Super Admin';

/** A role key as the role is named on screen: HR_ADMIN → "Company Admin". */
function roleWords(key: string): string {
  const known: Record<string, string> = {
    HR_ADMIN: 'Company Admin', HR_MANAGER: 'HR Manager', HR_EXECUTIVE: 'HR Executive', HR_VIEWER: 'HR Viewer',
    PLANT_HR: 'Plant HR', PLANT_ACCOUNTS: 'Plant Accounts', PLANT_HEAD: 'Plant Head', PLANT_COORDINATOR: 'Plant Coordinator',
    EHS_OFFICER: 'EHS Officer', PHYSICIAN: 'Physician', PLATFORM_OPS: 'Operations Admin', PLATFORM_FINANCE: 'Platform Finance',
    SUPPORT: 'Support', PARTNER_ADMIN: 'Venue Admin', PATHOLOGIST: 'Pathologist', LAB_TECHNICIAN: 'Lab Technician',
    HOSPITAL_COORDINATOR: 'Check-in Coordinator', HOSPITAL_TECHNICIAN: 'Hospital Technician',
  };
  if (known[key]) return known[key];
  const s = key.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
